import { DailyEvolutionItemDto } from "@babackapp/private-api-client/types";
import useTranslation from "@babackapp/translations/use-translation";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@babackapp/ui/components/card";
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@babackapp/ui/components/chart";
import { Bar, BarChart, CartesianGrid, Cell, XAxis } from "recharts";
import { getWeek } from "date-fns";
import useFormattedTimeRange from "../../home/lib/use-formatted-time-range";
import { deliveryColor, shippingColor, waitingColor } from "./colors";

type Props = {
  timeChartData: DailyEvolutionItemDto[];
};

export function ReturnRequestDailyStatusEvolutionChart({
  timeChartData,
}: Props) {
  const t = useTranslation();

  const timeChartConfig = {
    waiting: {
      label: t("operationsDashboard.status.waiting"),
      color: waitingColor,
    },
    shipping: {
      label: t("operationsDashboard.status.shipping"),
      color: shippingColor,
    },
    shipped: {
      label: t("operationsDashboard.status.shipped"),
      color: deliveryColor,
    },
  } satisfies ChartConfig;

  const getTopNonZeroBar = (data: DailyEvolutionItemDto[]) => {
    let topBarKey = null;
    Object.keys(data).forEach((key) => {
      if (
        key !== "date" &&
        +data[key as keyof typeof data]?.count > 0 // Handle count values
      ) {
        topBarKey = key;
      }
    });

    return topBarKey;
  };

  timeChartData ??= [];

  const formattedTimeRange = useFormattedTimeRange();

  return (
    <Card className="col-span-3 flex-1">
      <CardHeader>
        <CardTitle className="text-md">
          {t("operationsDashboard.evolutionTitle")}
        </CardTitle>
        <CardDescription>{formattedTimeRange}</CardDescription>
      </CardHeader>
      <CardContent className="h-80 w-full">
        <ChartContainer config={timeChartConfig} className="h-full w-full">
          <BarChart
            accessibilityLayer
            data={timeChartData}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <ChartTooltip
              content={
                <ChartTooltipContent
                  hideLabel={false}
                  formatter={(value, name, item) => {
                    if (typeof name !== "string") return null;

                    const [key] = name.split(".");
                    return (
                      <>
                        <div className="flex gap-2 items-center">
                          <div
                            className="h-2.5 w-2.5 shrink-0 rounded-[2px] bg-(--color-bg)"
                            style={
                              {
                                "--color-bg":
                                  timeChartConfig[
                                    key as keyof typeof timeChartConfig
                                  ]?.color,
                              } as React.CSSProperties
                            }
                          />
                          <span>
                            {timeChartConfig[
                              key as keyof typeof timeChartConfig
                            ]?.label || key}
                            {": "}
                            <span className="font-semibold">{value} </span>
                          </span>
                        </div>
                      </>
                    );
                  }}
                />
              }
            />
            <XAxis
              dataKey="date"
              tickLine={true}
              tickMargin={10}
              interval={3}
              tickFormatter={(value) => {
                const date = new Date(value);
                const week = getWeek(date);
                const result = week.toString();
                return result;
              }}
            />

            {Object.keys(timeChartConfig).map((key) => (
              <Bar
                key={key}
                dataKey={`${key}.count`}
                stackId="a"
                fill={
                  timeChartConfig[key as keyof typeof timeChartConfig].color
                }
              >
                {timeChartData.map((entry, index) => {
                  const topBarKey = getTopNonZeroBar(entry);
                  const isTopBar = topBarKey === key;
                  return (
                    <Cell
                      key={`cell-${index}`}
                      fill={
                        timeChartConfig[key as keyof typeof timeChartConfig]
                          .color
                      }
                      // @ts-expect-error not typed
                      radius={isTopBar ? [8, 8, 0, 0] : [0, 0, 0, 0]}
                    />
                  );
                })}
              </Bar>
            ))}
          </BarChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}
